import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Checkbox,
  FormHelperText
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import api from "../../services/api";
import officialCampaignService from "../../services/officialCampaignService";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  templateEditor: {
    fontFamily: "monospace",
    minHeight: 200
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  offlineDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: theme.spacing(1)
  }
}));

const defaultForm = {
  name: "",
  contactListProId: "",
  tagIds: [],
  whatsappId: "",
  whatsappIds: [], // Array de IDs para randomização
  ticketRegister: false,
  ticketStatus: "closed",
  ticketQueueId: "",
  ticketUserId: "",
  scheduledAt: "",
  templateName: "",
  templateLanguage: "",
  templateComponentsJson: "[]",
  messageInterval: 20, // Intervalo randômico de disparo em segundos
  longerIntervalAfter: 20, // Número de mensagens após as quais usar intervalo maior
  greaterInterval: 60, // Intervalo de disparo maior em segundos
};

const OfficialCampaignModal = ({ open, onClose, onSuccess }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [form, setForm] = useState(defaultForm);
  const [contactLists, setContactLists] = useState([]);
  const [tags, setTags] = useState([]);
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const companyId = user?.companyId;

  useEffect(() => {
    if (!open || !companyId) {
      // Reset form quando modal fechar
      if (!open) {
        setForm(defaultForm);
        setSelectedTemplate(null);
        setTemplates([]);
      }
      return;
    }

    const fetchData = async () => {
      try {
        const [{ data: lists }, { data: tagList }, { data: queueData }, { data: usersData }, { data: whatsappData }] = await Promise.all([
          api.get("/contact-lists-pro/list", { params: { companyId } }),
          api.get("/tags/list", { params: { companyId, kanban: 0 } }),
          api.get("/queue"),
          api.get("/users/"),
          api.get("/whatsapp", { params: { companyId, session: 0 } })
        ]);

        setContactLists(lists || []);
        setTags(tagList || []);
        setQueues(queueData || []);
        setUsers(usersData?.users || []);
        
        const officials = (whatsappData || []).filter(w => w.channel === "whatsapp_oficial");
        setWhatsapps(officials);
      } catch (err) {
        toastError(err);
      }
    };

    fetchData();
  }, [open, companyId]);

  const handleChange = useCallback((field) => (event) => {
    const value = event?.target?.value;
    if (field === "ticketRegister") {
      setForm(prev => ({ ...prev, ticketRegister: event.target.checked }));
    } else {
      setForm(prev => ({ ...prev, [field]: value }));
    }
  }, []);

  const handleSelectTemplate = useCallback((template) => {
    setSelectedTemplate(template);
    setForm(prev => ({
      ...prev,
      templateName: template?.name || "",
      templateLanguage: template?.language || "",
      templateComponentsJson: JSON.stringify(template?.components || [], null, 2)
    }));
  }, []);

  const handleLoadTemplates = useCallback(async (whatsappId) => {
    if (!whatsappId) {
      setTemplates([]);
      return;
    }
    setLoadingTemplates(true);
    try {
      const { data } = await officialCampaignService.templates(whatsappId);
      setTemplates(data?.data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const estimateContacts = useMemo(() => {
    if (form.contactListProId) {
      const selected = contactLists.find(list => list.id === form.contactListProId);
      // Tentar diferentes campos que podem conter o count
      if (selected?.contactsCount !== undefined) return selected.contactsCount;
      if (selected?.total !== undefined) return selected.total;
      if (Array.isArray(selected?.contacts)) return selected.contacts.length;
      // Se não tiver, retornar null para indicar que precisa buscar
      return null;
    }

    if (form.tagIds?.length) {
      const total = form.tagIds.reduce((acc, tagId) => {
        const tag = tags.find(item => item.id === tagId);
        if (!tag) return acc;
        if (Array.isArray(tag.contacts)) {
          return acc + tag.contacts.length;
        }
        if (tag.count !== undefined) return acc + tag.count;
        return acc;
      }, 0);
      return total > 0 ? total : null;
    }

    return null;
  }, [form.contactListProId, form.tagIds, contactLists, tags]);

  const estimatedDuration = useMemo(() => {
    if (!estimateContacts || estimateContacts === null) return "—";
    // Usar intervalo padrão de 20 segundos (mesmo das configurações de campanha)
    const intervalSeconds = 20;
    const totalSeconds = estimateContacts * intervalSeconds;
    const minutes = Math.ceil(totalSeconds / 60);
    if (minutes < 1) return "< 1 min";
    return `${minutes} min`;
  }, [estimateContacts]);

  const connectionStatus = useMemo(() => {
    // Verificar status do primeiro WhatsApp selecionado
    const firstWhatsappId = form.whatsappIds && form.whatsappIds.length > 0 
      ? form.whatsappIds[0] 
      : form.whatsappId;
    const conn = whatsapps.find(item => item.id === firstWhatsappId);
    if (!conn) return null;
    return conn.status;
  }, [whatsapps, form.whatsappId, form.whatsappIds]);

  const resetForm = () => {
    setForm(defaultForm);
    setSelectedTemplate(null);
    setTemplates([]);
  };

  // Carregar whatsappIds quando o modal abrir (se houver campanha para editar)
  // Nota: Este modal parece ser apenas para criação, mas mantemos compatibilidade

  const handleSubmit = async () => {
    try {
      // Validar whatsappIds ou whatsappId
      if ((!form.whatsappIds || form.whatsappIds.length === 0) && !form.whatsappId) {
        toast.error("Selecione pelo menos uma conexão WhatsApp.");
        return;
      }

      if (!selectedTemplate) {
        toast.error("Selecione um template.");
        return;
      }

      const templateComponents = JSON.parse(form.templateComponentsJson || "[]");

      // Se tem whatsappIds, usar o primeiro como whatsappId para compatibilidade
      const selectedWhatsappId = form.whatsappIds.length > 0 ? form.whatsappIds[0] : form.whatsappId;

      const payload = {
        name: form.name || selectedTemplate.name,
        contactListId: null,
        contactListProId: form.contactListProId || null,
        tagIds: form.tagIds,
        whatsappId: selectedWhatsappId, // Mantido para compatibilidade
        whatsappIds: form.whatsappIds.length > 0 ? JSON.stringify(form.whatsappIds) : null, // Array de IDs para randomização
        templateName: selectedTemplate.name,
        templateLanguage: selectedTemplate.language,
        templateComponents,
        ticketRegister: form.ticketRegister,
        ticketStatus: form.ticketStatus || "closed",
        ticketQueueId: form.ticketQueueId || null,
        ticketUserId: form.ticketUserId || null,
        scheduledAt: form.scheduledAt || null,
        messageInterval: form.messageInterval || 20,
        longerIntervalAfter: form.longerIntervalAfter || 20,
        greaterInterval: form.greaterInterval || 60
      };

      setLoading(true);
      const { data } = await officialCampaignService.create(payload);
      await officialCampaignService.schedule(data.id, {
        scheduledAt: form.scheduledAt || null
      });

      toast.success("Campanha oficial agendada com sucesso!");
      resetForm();
      onSuccess?.();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Nova campanha oficial</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>Público-alvo & Conexão</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Nome da campanha"
              fullWidth
              value={form.name}
              onChange={handleChange("name")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="contact-list-label">Lista de Contatos PRO</InputLabel>
              <Select
                labelId="contact-list-label"
                value={form.contactListProId}
                onChange={handleChange("contactListProId")}
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {contactLists.map(list => (
                  <MenuItem key={list.id} value={list.id}>
                    {list.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="tags-label">Tags (múltiplas)</InputLabel>
              <Select
                labelId="tags-label"
                multiple
                value={form.tagIds}
                onChange={handleChange("tagIds")}
                renderValue={selected => (
                  <Box display="flex" flexWrap="wrap" gap={8}>
                    {selected.map(value => {
                      const tag = tags.find(t => t.id === value);
                      return <Chip key={value} label={tag?.name || value} size="small" />;
                    })}
                  </Box>
                )}
              >
                {tags.map(tag => (
                  <MenuItem key={tag.id} value={tag.id}>
                    {tag.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="whatsapp-label">WhatsApps para Randomização *</InputLabel>
              <Select
                labelId="whatsapp-label"
                multiple
                value={form.whatsappIds || []}
                onChange={(event) => {
                  const selectedIds = event.target.value;
                  setForm(prev => {
                    const newForm = { ...prev, whatsappIds: selectedIds };
                    // Manter compatibilidade: usar o primeiro como whatsappId
                    if (selectedIds.length > 0) {
                      newForm.whatsappId = selectedIds[0];
                      handleLoadTemplates(selectedIds[0]);
                    } else {
                      newForm.whatsappId = "";
                    }
                    return newForm;
                  });
                }}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return "Selecione os WhatsApps";
                  }
                  if (selected.length === 1) {
                    const whatsapp = whatsapps.find(w => w.id === selected[0]);
                    return whatsapp ? (whatsapp.name || whatsapp.phone_number) : `WhatsApp ${selected[0]}`;
                  }
                  return `${selected.length} WhatsApps selecionados`;
                }}
              >
                {whatsapps.map(w => {
                  const isConnected = w.status === "CONNECTED";
                  return (
                    <MenuItem key={w.id} value={w.id}>
                      <Checkbox checked={(form.whatsappIds || []).indexOf(w.id) > -1} />
                      <Box display="flex" alignItems="center" flex={1}>
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: isConnected ? "#4caf50" : "#f44336",
                            display: "inline-block",
                            marginRight: 8,
                          }}
                        />
                        {w.name || w.phone_number}
                        <Typography
                          variant="caption"
                          style={{
                            marginLeft: 8,
                            color: isConnected ? "#4caf50" : "#f44336",
                            fontWeight: "bold",
                          }}
                        >
                          {isConnected ? "Conectado" : "Desconectado"}
                        </Typography>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
              <FormHelperText>
                Selecione múltiplos WhatsApps para randomização automática. Apenas WhatsApps conectados serão usados.
              </FormHelperText>
            </FormControl>
            {form.whatsappIds && form.whatsappIds.length > 0 && (
              <Box mt={1}>
                <span
                  className={classes.offlineDot}
                  style={{
                    backgroundColor: connectionStatus === "CONNECTED" ? "#4caf50" : "#f44336"
                  }}
                />
                <Typography variant="body2" component="span">
                  {connectionStatus === "CONNECTED" ? "Online" : "Offline"}
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Configurações de Intervalo */}
          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>Intervalos de Disparo</Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="messageInterval-label">Intervalo Randômico de Disparo</InputLabel>
              <Select
                labelId="messageInterval-label"
                value={form.messageInterval}
                onChange={(event) => {
                  setForm(prev => ({ ...prev, messageInterval: event.target.value }));
                }}
              >
                <MenuItem value={0}>Sem Intervalo</MenuItem>
                <MenuItem value={5}>5 segundos</MenuItem>
                <MenuItem value={10}>10 segundos</MenuItem>
                <MenuItem value={15}>15 segundos</MenuItem>
                <MenuItem value={20}>20 segundos</MenuItem>
                <MenuItem value={30}>30 segundos</MenuItem>
                <MenuItem value={40}>40 segundos</MenuItem>
                <MenuItem value={60}>60 segundos</MenuItem>
                <MenuItem value={80}>80 segundos</MenuItem>
                <MenuItem value={100}>100 segundos</MenuItem>
                <MenuItem value={120}>120 segundos</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="longerIntervalAfter-label">Intervalo maior após</InputLabel>
              <Select
                labelId="longerIntervalAfter-label"
                value={form.longerIntervalAfter}
                onChange={(event) => {
                  setForm(prev => ({ ...prev, longerIntervalAfter: event.target.value }));
                }}
              >
                <MenuItem value={0}>Indefinido</MenuItem>
                <MenuItem value={5}>5 mensagens</MenuItem>
                <MenuItem value={10}>10 mensagens</MenuItem>
                <MenuItem value={15}>15 mensagens</MenuItem>
                <MenuItem value={20}>20 mensagens</MenuItem>
                <MenuItem value={30}>30 mensagens</MenuItem>
                <MenuItem value={40}>40 mensagens</MenuItem>
                <MenuItem value={50}>50 mensagens</MenuItem>
                <MenuItem value={60}>60 mensagens</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="greaterInterval-label">Intervalo de disparo maior</InputLabel>
              <Select
                labelId="greaterInterval-label"
                value={form.greaterInterval}
                onChange={(event) => {
                  setForm(prev => ({ ...prev, greaterInterval: event.target.value }));
                }}
              >
                <MenuItem value={0}>Sem Intervalo</MenuItem>
                <MenuItem value={20}>20 segundos</MenuItem>
                <MenuItem value={30}>30 segundos</MenuItem>
                <MenuItem value={40}>40 segundos</MenuItem>
                <MenuItem value={50}>50 segundos</MenuItem>
                <MenuItem value={60}>60 segundos</MenuItem>
                <MenuItem value={70}>70 segundos</MenuItem>
                <MenuItem value={80}>80 segundos</MenuItem>
                <MenuItem value={90}>90 segundos</MenuItem>
                <MenuItem value={100}>100 segundos</MenuItem>
                <MenuItem value={110}>110 segundos</MenuItem>
                <MenuItem value={120}>120 segundos</MenuItem>
                <MenuItem value={130}>130 segundos</MenuItem>
                <MenuItem value={140}>140 segundos</MenuItem>
                <MenuItem value={150}>150 segundos</MenuItem>
                <MenuItem value={160}>160 segundos</MenuItem>
                <MenuItem value={170}>170 segundos</MenuItem>
                <MenuItem value={180}>180 segundos</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>Configurações de Tickets</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.ticketRegister}
                  onChange={handleChange("ticketRegister")}
                  color="primary"
                />
              }
              label="Registrar no sistema"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="ticket-status-label">Status do ticket</InputLabel>
              <Select
                labelId="ticket-status-label"
                value={form.ticketStatus}
                onChange={handleChange("ticketStatus")}
              >
                <MenuItem value="closed">Encerrado</MenuItem>
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="open">Aberto</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="queue-label">Fila</InputLabel>
              <Select
                labelId="queue-label"
                value={form.ticketQueueId}
                onChange={handleChange("ticketQueueId")}
              >
                <MenuItem value="">
                  <em>Sem fila</em>
                </MenuItem>
                {queues.map(queue => (
                  <MenuItem key={queue.id} value={queue.id}>
                    {queue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="user-label">Usuário responsável</InputLabel>
              <Select
                labelId="user-label"
                value={form.ticketUserId}
                onChange={handleChange("ticketUserId")}
              >
                <MenuItem value="">
                  <em>Automático</em>
                </MenuItem>
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Agendar execução"
              InputLabelProps={{ shrink: true }}
              value={form.scheduledAt}
              onChange={handleChange("scheduledAt")}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>Template da campanha</Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="template-label">Selecione o template</InputLabel>
              <Select
                labelId="template-label"
                value={selectedTemplate?.name || ""}
                onChange={(event) => {
                  const templateName = event.target.value;
                  const template = templates.find(t => t.name === templateName);
                  if (template) {
                    handleSelectTemplate(template);
                  }
                }}
                disabled={!templates.length || loadingTemplates}
              >
                <MenuItem value="">
                  <em>{loadingTemplates ? "Carregando..." : "Selecione"}</em>
                </MenuItem>
                {templates.map(template => (
                  <MenuItem key={template.id} value={template.name}>
                    {template.name} ({template.language})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Componentes do template (JSON)"
              multiline
              fullWidth
              minRows={6}
              variant="outlined"
              value={form.templateComponentsJson}
              onChange={handleChange("templateComponentsJson")}
              className={classes.templateEditor}
            />
            <Typography variant="caption" color="textSecondary">
              Use placeholders como {"{{contact.name}}"} para personalizar dinamicamente.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box mt={2} p={2} border={1} borderColor="divider" borderRadius={8}>
              <Typography className={classes.sectionTitle}>Resumo</Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Total estimado de contatos</Typography>
                <Typography variant="body1" color="primary">
                  {estimateContacts !== null ? `${estimateContacts} contatos` : "—"}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Tempo estimado</Typography>
                <Typography variant="body1" color="primary">
                  {estimatedDuration}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">WhatsApp</Typography>
                <Typography variant="body1" color="primary">
                  {whatsapps.find(w => w.id === form.whatsappId)?.name || "—"}
                </Typography>
              </Box>
              <Typography variant="caption" color="textSecondary">
                A campanha será executada imediatamente após salvar (ou no horário programado).
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          Cancelar
        </Button>
        <Button color="primary" variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OfficialCampaignModal;

